import {
    useEffect,
    useRef,
    type FC
} from "react";
import {
    StyleSheet,
    type View,
    Platform
} from "react-native";
import type IToggleGroupItemProps from "./type";
import type {
    ToggleGroupItemKeyboardEventType,
    ToggleGroupItemWebPropsType
} from "./type";
import {
    useToggleGroupContext
} from "../../context";
import {
    TOGGLE_GROUP_NAVIGATION_KEYS,
    TOGGLE_GROUP_PART
} from "../../util";
import {
    warnOnce
} from "../../../../utils/development";
import Toggle from "../../../toggle";

const stylesheet = StyleSheet.create({
    stretched: {
        flex: 1
    }
});

const ToggleGroupItem: FC<IToggleGroupItemProps> & {
    toggleGroupPart: string;
} = ({
    onFocus: onFocusProp,
    isDisabled = false,
    customTheme,
    value,
    ...props
}) => {
    const context = useToggleGroupContext();

    const itemRef = useRef<View>(null);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "ToggleGroup.Item: must be rendered inside <ToggleGroup>."
        });
    }, [
        context
    ]);

    useEffect(() => {
        return context?.registerItem(value, {
            ref: itemRef,
            isDisabled
        });
    }, [
        context?.registerItem,
        isDisabled,
        value
    ]);

    if(!context) {
        return null;
    }

    const onFocus: NonNullable<IToggleGroupItemProps["onFocus"]> = (event) => {
        context.handleFocus(value);

        onFocusProp?.(event);
    };

    const handleChange = () => {
        context.handleToggle(value);
    };

    const onKeyDown = (event: ToggleGroupItemKeyboardEventType) => {
        if(event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        if(!TOGGLE_GROUP_NAVIGATION_KEYS.includes(event.key)) {
            return;
        }

        if(context.handleNavigate({
            key: event.key,
            value
        })) {
            event.preventDefault();
        }
    };

    const webProps: ToggleGroupItemWebPropsType = Platform.OS === "web" ? {
        tabIndex: context.tabStopValue === value && !isDisabled ? 0 : -1,
        onKeyDown
    } : {};

    return <Toggle
        {...props}
        {...webProps}
        style={[
            props.style,
            context.isStretched ? stylesheet.stretched : undefined
        ]}
        isPressScaleEnabled={context.isPressScaleEnabled}
        customTheme={customTheme ?? context.customTheme}
        isPressed={context.values.includes(value)}
        onPressedChange={handleChange}
        isDisabled={isDisabled}
        onFocus={onFocus}
        ref={itemRef}
    />;
};
ToggleGroupItem.toggleGroupPart = TOGGLE_GROUP_PART;
export default ToggleGroupItem;
